// =====================================================
// 📁 useProductCatalog.ts
// =====================================================

import { useDispatch, useSelector }
    from 'react-redux';

import {
    RootState,
    AppDispatch
} from '../../store/store';

import {
    setFilteredProducts,
    setCategories,
    setSubCategories,
    setBrands,
    setPageSize,
    setIsLoading,
    setCurrentFilter,
    clearProductPanelState,
    setCurrentPage,
    setSelectedCategoryId,
    setSelectedSubCategoryId,
    setSelectedBrandId,
} from '../../store/slices/productPanel/productPanelSlice';

import { ProductPanelFilter }
    from '../../types/productpanel/ProductPanelFilter';

import ProductPanelService
    from '../../services/product/ProductPanelService';

const useProductCatalog = (
    branchId?: number
) => {

    // =====================================================
    // 🔹 Redux
    // =====================================================

    const dispatch: AppDispatch =
        useDispatch();

    const {
        filteredProducts,
        categories,
        subCategories,
        brands,
        productsByCategory,
        paginator,
        pageSize,
        currentPage,
        selectedCategoryId,
        selectedSubCategoryId,
        selectedBrandId,
        isLoading,
        currentFilter,
    } = useSelector(
        (state: RootState) =>
            state.productPanel
    );

    // =====================================================
    // 🔹 Products
    // =====================================================

    const fetchFilteredProducts = async (

        filters: ProductPanelFilter,

        page: number = 0,

        size: number = pageSize

    ) => {

        try {

            dispatch(
                setIsLoading(true)
            );

            const response =
                await ProductPanelService
                    .getFilteredProducts(
                        filters,
                        page,
                        size
                    );

            if (response?.content) {

                dispatch(
                    setFilteredProducts(
                        mapPaginatedResponse(response)
                    )
                );

                dispatch(
                    setCurrentFilter(filters)
                );

            }

        } catch (error) {

            console.error(
                '❌ Error al filtrar productos:',
                error
            );

        } finally {

            dispatch(
                setIsLoading(false)
            );

        }

    };

    // =====================================================
    // 🔹 Categories
    // =====================================================

    const fetchCategories = async () => {

        try {

            if (!branchId) {
                return;
            }

            const response =
                await ProductPanelService
                    .getAvailableCategories(
                        branchId
                    );

            dispatch(
                setCategories(response)
            );

        } catch (error) {

            console.error(
                '❌ Error al obtener categorías:',
                error
            );

        }

    };

    const fetchSubCategories = async (
        categoryId: number
    ) => {

        try {

            if (!branchId) {
                return;
            }

            const response =
                await ProductPanelService
                    .getAvailableSubCategories(
                        branchId,
                        categoryId
                    );

            dispatch(
                setSubCategories(
                    response
                )
            );

        } catch (error) {

            console.error(
                '❌ Error al obtener subcategorías:',
                error
            );

        }

    };

    const fetchBrands = async () => {

        try {

            if (!branchId) {
                return;
            }

            const response =
                await ProductPanelService
                    .getAvailableBrands(
                        branchId
                    );

            dispatch(
                setBrands(
                    response
                )
            );

        } catch (error) {

            console.error(
                '❌ Error al obtener marcas:',
                error
            );

        }

    };

    // =====================================================
    // 🔹 Helpers
    // =====================================================

    const mapPaginatedResponse = (
        response: any
    ) => ({

        content: response.content,

        paginator: {

            number: response.number,

            size: response.size,

            totalPages: response.totalPages,

            totalElements:
                response.totalElements,

            first: response.first,

            last: response.last,

        },

    });

    // =====================================================
    // 🔹 Pagination
    // =====================================================

    const updatePageSize = (
        size: number
    ) => {

        dispatch(
            setPageSize(size)
        );

    };

    const updateCurrentPage = (
        page: number
    ) => {

        dispatch(
            setCurrentPage(page)
        );

    };

    // =====================================================
    // 🔹 Category
    // =====================================================

    const setSelectedCategory = (
        id: number | null
    ) => {

        dispatch(
            setSelectedCategoryId(id)
        );

    };

    const setSelectedSubCategory = (
        id: number | null
    ) => {

        dispatch(
            setSelectedSubCategoryId(id)
        );

    };

    const setSelectedBrand = (
        id: number | null
    ) => {

        dispatch(
            setSelectedBrandId(id)
        );

    };

    // =====================================================
    // 🔹 Reset
    // =====================================================

    const resetProductPanelState = () => {

        dispatch(
            clearProductPanelState()
        );

    };

    // =====================================================
    // ✅ Return
    // =====================================================

    return {

        filteredProducts,
        categories,
        subCategories,
        brands,
        productsByCategory,
        paginator,
        pageSize,
        currentPage,
        selectedCategoryId,
        selectedSubCategoryId,
        selectedBrandId,
        isLoading,
        currentFilter,
        fetchFilteredProducts,
        fetchCategories,
        fetchSubCategories,
        fetchBrands,
        updatePageSize,
        updateCurrentPage,
        setSelectedCategory,
        setSelectedSubCategory,
        setSelectedBrand,
        resetProductPanelState,

    };

};

export default useProductCatalog;